<template>
    <v-container class="agent-settings mt-6 pb-12">
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
            <div>
                <h1 class="text-h5 font-weight-bold mb-1">Agent Settings</h1>
                <div class="text-body-2 grey--text text--darken-1">
                    Tracking options for the DG Tasks desktop app. Every running app checks for changes once an
                    hour, so a change reaches everyone within the hour without reinstalling.
                </div>
            </div>
            <v-spacer />
            <v-btn outlined class="text-none" :disabled="loading || saving || !hasNonDefault" @click="resetAll">
                <v-icon left small>mdi-restore</v-icon>
                Reset all to defaults
            </v-btn>
            <v-btn color="primary" depressed class="text-none" :loading="saving" :disabled="!dirtyKeys.length"
                @click="save">
                <v-icon left small>mdi-content-save</v-icon>
                Save {{ dirtyKeys.length ? `(${dirtyKeys.length})` : '' }}
            </v-btn>
        </div>

        <v-alert v-if="error" type="error" dense outlined class="mt-4">{{ error }}</v-alert>

        <div v-if="loading" class="d-flex justify-center my-12">
            <v-progress-circular indeterminate color="primary" />
        </div>

        <v-card v-for="group in groups" v-else :key="group.name" outlined class="mt-6">
            <v-card-title class="text-subtitle-1 font-weight-bold py-3">{{ group.name }}</v-card-title>
            <v-divider />
            <div v-for="(setting, i) in group.settings" :key="setting.key">
                <v-divider v-if="i > 0" />
                <div class="setting-row d-flex flex-wrap align-start pa-4">
                    <div class="setting-text">
                        <div class="font-weight-medium">
                            {{ setting.label }}
                            <v-chip v-if="isChanged(setting)" x-small label color="orange lighten-4"
                                class="ml-2 orange--text text--darken-3">
                                Default {{ formatValue(setting, setting.default_value) }}
                            </v-chip>
                        </div>
                        <div class="text-body-2 grey--text text--darken-1 mt-1">{{ setting.description }}</div>
                        <div v-if="setting.updated_by_name" class="text-caption grey--text mt-1">
                            Changed by {{ setting.updated_by_name }} {{ $date(setting.updated_at).fromNow() }}
                        </div>
                    </div>
                    <div class="setting-input d-flex align-center">
                        <v-text-field v-model="draft[setting.key]" type="number" dense outlined hide-details="auto"
                            :step="setting.type === 'float' ? 0.05 : 1" :min="setting.min" :max="setting.max"
                            :suffix="setting.unit" :rules="[v => validate(setting, v)]"
                            :class="{ dirty: dirtyKeys.includes(setting.key) }" />
                        <v-btn icon small class="ml-1" :disabled="isDraftDefault(setting)"
                            title="Use default" @click="draft[setting.key] = String(setting.default_value)">
                            <v-icon small>mdi-restore</v-icon>
                        </v-btn>
                    </div>
                </div>
            </div>
        </v-card>
    </v-container>
</template>

<script>
export default {
    name: 'AgentSettingsComponent',
    data() {
        return {
            settings: [],
            draft: {},
            loading: true,
            saving: false,
            error: '',
        }
    },
    computed: {
        groups() {
            const groups = []
            for (const setting of this.settings) {
                let group = groups.find(g => g.name === setting.group)
                if (!group) {
                    group = { name: setting.group, settings: [] }
                    groups.push(group)
                }
                group.settings.push(setting)
            }
            return groups
        },
        dirtyKeys() {
            return this.settings
                .filter(s => this.draft[s.key] !== undefined && Number(this.draft[s.key]) !== s.value)
                .map(s => s.key)
        },
        hasNonDefault() {
            return this.settings.some(s => !this.isDraftDefault(s))
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            this.loading = true
            this.error = ''
            const res = await this.$axios.get('api/agent/settings/admin')
            if (res.data?.response) {
                this.setSettings(res.data.settings)
            } else {
                this.error = res.data?.message || 'Could not load the agent settings.'
            }
            this.loading = false
        },
        setSettings(settings) {
            this.settings = settings
            this.draft = Object.fromEntries(settings.map(s => [s.key, String(s.value)]))
        },
        validate(setting, value) {
            if (value === '' || value === null || isNaN(Number(value))) return 'Enter a number'
            const n = Number(value)
            if (setting.type === 'int' && !Number.isInteger(n)) return 'Whole numbers only'
            if (n < setting.min || n > setting.max) return `${setting.min} to ${setting.max}`
            return true
        },
        isChanged(setting) {
            return setting.value !== setting.default_value
        },
        isDraftDefault(setting) {
            return Number(this.draft[setting.key]) === setting.default_value
        },
        formatValue(setting, value) {
            return setting.unit ? `${value} ${setting.unit}` : `${value}`
        },
        resetAll() {
            for (const setting of this.settings) {
                this.draft[setting.key] = String(setting.default_value)
            }
        },
        async save() {
            const invalid = this.settings.find(s => this.dirtyKeys.includes(s.key) && this.validate(s, this.draft[s.key]) !== true)
            if (invalid) {
                this.$toast.error(`${invalid.label}: ${this.validate(invalid, this.draft[invalid.key])}`)
                return
            }

            const changes = Object.fromEntries(this.dirtyKeys.map(key => [key, Number(this.draft[key])]))
            this.saving = true
            const res = await this.$axios.post('api/agent/settings', { settings: changes })
            this.saving = false

            if (res.data?.response) {
                this.setSettings(res.data.settings)
                this.$toast.success('Agent settings saved. Apps pick them up within the hour.')
            } else {
                this.$toast.error(res.data?.message || 'Could not save the agent settings.')
            }
        },
    },
}
</script>

<style scoped>
.setting-text {
    flex: 1 1 360px;
    min-width: 0;
    padding-right: 24px;
}

.setting-input {
    flex: 0 0 240px;
    max-width: 100%;
}

.dirty >>> fieldset {
    border-color: #fb8c00;
    border-width: 2px;
}
</style>
